import { useEffect, useState } from "react";
import OutreachComposer from "../student_care/OutreachComposer";

// Score-overview modal for one diagnostic-funnel candidate. Driven entirely by
// props — the open/close lifecycle is owned by the DiagnosticFunnel host so the
// roster mounts a single React root with a single modal, not one island per row.
// Opening it fetches the candidate's score report (the same DiagnosticReportBuilder
// data they saw), shows the tier's recommended action, and embeds the outreach
// composer so the admin can act without leaving the roster. Nothing here writes —
// the composer's send is a normal form POST the server owns.
function pillClass(tone, size = "--sm") {
  const suffix = { err: " --err", warn: " --warn", ok: " --ok", strong: " --strong" }[tone] || "";
  return `admin-pill ${size}${suffix}`;
}

// Inline modal layout so the island doesn't depend on stylesheet rules that
// don't exist yet — everything else reuses existing admin-* component classes.
const OVERLAY_STYLE = {
  position: "fixed", inset: 0, zIndex: 1000,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "5vh 16px", overflowY: "auto",
};
const MODAL_STYLE = {
  background: "var(--surface, #fff)", borderRadius: 14,
  width: "min(720px, 100%)", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};
const HEADER_STYLE = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  gap: 12, padding: "18px 22px", borderBottom: "1px solid var(--ink-100)",
  position: "sticky", top: 0, background: "var(--surface, #fff)", zIndex: 1,
};

export default function OverviewModal({
  row,
  csrf_token,
  intents = [],
  send_endpoint,
  onClose,
}) {
  const {
    detail_endpoint,
    draft_endpoint,
    candidate_profile_id,
    name,
    first_name = "there",
    default_intent,
    tier_label,
    score,
    band_label,
    band_tone,
    action_headline,
    action_body,
  } = row;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(detail_endpoint, { headers: { Accept: "application/json" } });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || "Couldn't load this candidate's report.");
          return;
        }
        setDetail(data);
      } catch {
        if (!cancelled) setError("Couldn't reach the server — try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detail_endpoint]);

  const plan = detail?.recommended_plan;

  return (
    <div style={OVERLAY_STYLE} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={MODAL_STYLE} onClick={(e) => e.stopPropagation()}>
        <div style={HEADER_STYLE}>
          <div>
            <div className="admin-card__title" style={{ fontSize: 18 }}>{name}</div>
            <div style={{ marginTop: 4 }}>
              <span className={pillClass(band_tone)}>{band_label} · {score}%</span>
              <span className="admin-table__muted" style={{ marginLeft: 8 }}>{tier_label}</span>
            </div>
          </div>
          <button type="button" className="admin-btn --ghost --sm" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div style={{ padding: "20px 22px" }}>
          {/* Tier recommended action — drives the email below */}
          <div className="admin-card --flat" style={{ marginBottom: "var(--space-4)" }}>
            <div className="admin-card__title" style={{ fontSize: 15 }}>{action_headline}</div>
            <div className="admin-card__sub">{action_body}</div>
          </div>

          {loading && <div className="admin-table__muted">Loading report…</div>}
          {error && <div className="admin-form-group__hint" style={{ color: "var(--err)" }}>{error}</div>}

          {detail && (
            <>
              <div className="admin-activity__pulse-grid" style={{ marginBottom: "var(--space-4)" }}>
                <Stat label="PROJECTED SCORE" value={`${detail.score}%`} />
                <Stat label="GAP TO PASS" value={detail.gap_to_pass > 0 ? `${detail.gap_to_pass} pts` : "Passed"} />
                <Stat
                  label="PERCENTILE"
                  value={detail.percentile != null ? `${detail.percentile}th` : "—"}
                />
                <Stat
                  label="EXAM IN"
                  value={detail.days_to_exam != null ? `${detail.days_to_exam} days` : "—"}
                />
              </div>

              {detail.weak_topics?.length > 0 && (
                <Section title="Weakest subjects" sub="Where the gap to a pass lives">
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {detail.weak_topics.map((w) => (
                      <Bar key={w.rank} label={w.subject} percent={w.percent} note={`+${w.est_gain} pts if mastered`} />
                    ))}
                  </div>
                </Section>
              )}

              {detail.subject_breakdown?.length > 0 && (
                <Section title="All subjects" sub="Strongest first">
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {detail.subject_breakdown.map((s) => (
                      <Bar
                        key={s.subject}
                        label={s.subject}
                        percent={s.percent}
                        note={
                          <span className={pillClass(s.status_tone)}>{s.status_label}</span>
                        }
                      />
                    ))}
                  </div>
                </Section>
              )}

              {plan && (
                <Section title="Recommended plan" sub="The plan that fits their readiness and timeline">
                  <div className="admin-card --flat" style={{ background: "var(--ink-50)" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      <strong style={{ color: "var(--ink-900)", fontSize: 15 }}>{plan.name}</strong>
                      <span className="admin-table__muted">{plan.price_label} · {plan.duration_label}</span>
                    </div>
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "var(--ink-700)", fontSize: 13 }}>
                      {(plan.features || []).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                    {plan.paystack_url && (
                      <p className="admin-form-group__hint" style={{ marginTop: 8 }}>
                        Checkout link to share:{" "}
                        <a href={plan.paystack_url} target="_blank" rel="noreferrer">{plan.paystack_url}</a>
                      </p>
                    )}
                  </div>
                </Section>
              )}

              {detail.seven_day_plan?.length > 0 && (
                <Section title="7-day plan" sub="The roadmap they were shown">
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {detail.seven_day_plan.map((row, i) => (
                      <div key={i} style={{ fontSize: 13, color: "var(--ink-700)" }}>
                        <strong style={{ color: "var(--ink-900)" }}>{row.label}</strong> — {row.detail}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}

          {/* Outreach — the conversion action. Reuses the Student Care composer. */}
          <Section title="Send a personal message" sub="Your words, your name — Meducative just delivers it">
            <OutreachComposer
              draft_endpoint={draft_endpoint}
              send_endpoint={send_endpoint}
              candidate_profile_id={candidate_profile_id}
              csrf_token={csrf_token}
              first_name={first_name}
              intents={intents}
              default_intent={default_intent}
              source="diagnostic_funnel"
            />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="admin-stat">
      <div className="admin-lbl">{label}</div>
      <div className="admin-stat__value" style={{ fontSize: 18 }}>{value}</div>
    </div>
  );
}

function Section({ title, sub, children }) {
  return (
    <section style={{ marginBottom: "var(--space-4)" }}>
      <div className="admin-card__title" style={{ fontSize: 14 }}>{title}</div>
      {sub && <div className="admin-card__sub" style={{ marginBottom: 8 }}>{sub}</div>}
      {children}
    </section>
  );
}

function Bar({ label, percent, note }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 120px", alignItems: "center", gap: 10, fontSize: 13 }}>
      <span style={{ color: "var(--ink-900)" }}>{label}</span>
      <div style={{ height: 6, background: "var(--ink-100)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${percent}%`, height: "100%", background: "var(--accent)" }} />
      </div>
      <span className="admin-table__muted" style={{ textAlign: "right" }}>{note}</span>
    </div>
  );
}
